// src/components/Repositorio/FormularioArquivamentoPublico.jsx

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Chip,
  Stack,
} from '@mui/material';
import {
  CloudUpload,
  CloudUpload as UploadIcon,
  Add as AddIcon,
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
} from '@mui/icons-material';
import { arquivarDocumentoPublico } from '../../services/arquivoDigitalService';

const FormularioArquivamentoPublico = ({ onSuccess, userAuthenticated }) => {
  // Estados para controle do stepper
  const [etapaAtual, setEtapaAtual] = useState(0);
  const totalEtapas = 4;
  
  // Estado para loading e erros
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Estado para o upload do documento
  const [arquivo, setArquivo] = useState(null);
  const [tituloDocumento, setTituloDocumento] = useState('');
  const [resumo, setResumo] = useState('');
  
  // Estado para palavras-chave
  const [palavrasChave, setPalavrasChave] = useState([]);
  const [novaPalavraChave, setNovaPalavraChave] = useState('');
  
  // Estados para os novos campos
  const [dadosAutor, setDadosAutor] = useState({
    nomeAutor: '',
    biAutor: '',
    telefoneAutor: '',
    emailAutor: ''
  });

  const [dadosAcademicos, setDadosAcademicos] = useState({
    supervisor: '',
    coSupervisor: '',
    dataDefesa: new Date().toISOString().split('T')[0],
    areaConhecimento: '',
    programa: '',
    tipoTitulacao: 'Mestrado',
    tipoMaterial: 'Dissertação',
    instituicao: ''
  });

  const [aceitesLegais, setAceitesLegais] = useState({
    aceiteDireitosAutorais: false,
    aceiteLicencaDistribuicao: false
  });
  
  // Handlers para alterações nos campos
  const handleChangeDadosAutor = (e) => {
    const { name, value } = e.target;
    setDadosAutor({
      ...dadosAutor,
      [name]: value
    });
  };
  
  const handleChangeDadosAcademicos = (e) => {
    const { name, value } = e.target;
    setDadosAcademicos({
      ...dadosAcademicos,
      [name]: value
    });
  };
  
  const handleChangeAceitesLegais = (e) => {
    const { name, checked } = e.target;
    setAceitesLegais({
      ...aceitesLegais,
      [name]: checked
    });
  };
  
  // Handlers para palavras-chave
  const handleAdicionarPalavraChave = () => {
    if (novaPalavraChave.trim() !== '' && !palavrasChave.includes(novaPalavraChave.trim())) {
      setPalavrasChave([...palavrasChave, novaPalavraChave.trim()]);
      setNovaPalavraChave('');
    }
  };

  const handleRemoverPalavraChave = (index) => {
    const novasPalavrasChave = [...palavrasChave];
    novasPalavrasChave.splice(index, 1);
    setPalavrasChave(novasPalavrasChave);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdicionarPalavraChave();
    }
  };
  
  // Handler para upload de arquivo
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setArquivo(e.target.files[0]);
    }
  };
  
  // Navegar entre etapas
  const avancarEtapa = () => {
    if (etapaAtual < totalEtapas - 1) {
      setEtapaAtual(etapaAtual + 1);
    }
  };
  
  const voltarEtapa = () => {
    if (etapaAtual > 0) {
      setEtapaAtual(etapaAtual - 1);
    }
  };
  
  // Validações para habilitar o botão "Avançar"
  const validarEtapa1 = () => {
    return arquivo !== null && tituloDocumento.trim() !== '';
  };
  
  const validarEtapa2 = () => {
    return palavrasChave.length > 0;
  };
  
  const validarEtapa3 = () => {
    return dadosAutor.nomeAutor.trim() !== '' && dadosAutor.emailAutor.trim() !== '';
  };
  
  const validarEtapa4 = () => {
    return aceitesLegais.aceiteDireitosAutorais && aceitesLegais.aceiteLicencaDistribuicao;
  };
  
  const etapaValida = () => {
    switch (etapaAtual) {
      case 0: return validarEtapa1();
      case 1: return validarEtapa2();
      case 2: return validarEtapa3();
      case 3: return validarEtapa4();
      default: return false;
    }
  };
  
  // Enviar o formulário
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Criar FormData para envio de arquivo
      const formData = new FormData();
      formData.append('documento', arquivo);
      formData.append('titulo', tituloDocumento);
      formData.append('resumo', resumo);
      formData.append('palavrasChave', JSON.stringify(palavrasChave));
      
      // Adicionar dados do autor
      Object.keys(dadosAutor).forEach(key => {
        formData.append(key, dadosAutor[key]);
      });
      
      // Adicionar dados acadêmicos
      Object.keys(dadosAcademicos).forEach(key => {
        formData.append(key, dadosAcademicos[key]);
      });
      
      // Adicionar aceites legais
      Object.keys(aceitesLegais).forEach(key => {
        formData.append(key, aceitesLegais[key]);
      });
      
      // Enviar para API
      const response = await arquivarDocumentoPublico(formData);
      console.log('Documento arquivado com sucesso:', response);
      
      setSuccess(true);
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (err) {
      console.error('Erro ao arquivar documento:', err);
      setError(err.response?.data?.message || 'Erro ao arquivar documento no repositório');
    } finally {
      setLoading(false);
    }
  };
  
  // Renderização das etapas
  const renderEtapa1 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Upload do Documento</Typography>
      
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="Título do Documento"
              value={tituloDocumento}
              onChange={(e) => setTituloDocumento(e.target.value)}
              fullWidth
              required
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Resumo"
              value={resumo}
              onChange={(e) => setResumo(e.target.value)}
              fullWidth
              multiline
              rows={4}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Documento PDF
            </Typography>
            <Box 
              sx={{ 
                border: '1px dashed grey',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                mb: 2
              }}
            >
              <input
                accept=".pdf"
                style={{ display: 'none' }}
                id="documento-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="documento-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadIcon />}
                >
                  Selecionar PDF
                </Button>
              </label>
              
              {arquivo && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Arquivo selecionado: {arquivo.name}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
  
  const renderEtapa2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Palavras-chave</Typography>
      
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="body2" paragraph>
          Adicione palavras-chave para facilitar a busca deste documento no repositório.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Nova palavra-chave"
            value={novaPalavraChave}
            onChange={(e) => setNovaPalavraChave(e.target.value)}
            onKeyPress={handleKeyPress}
            size="small"
            fullWidth
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAdicionarPalavraChave}
            startIcon={<AddIcon />}
          >
            Adicionar
          </Button>
        </Box>
        
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {palavrasChave.map((palavra, index) => (
            <Chip
              key={index}
              label={palavra}
              onDelete={() => handleRemoverPalavraChave(index)}
              deleteIcon={<CloseIcon />}
            />
          ))}
          {palavrasChave.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nenhuma palavra-chave adicionada
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
  
  const renderEtapa3 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Dados do Autor e Informações Acadêmicas</Typography>
      
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>Identificação do Autor</Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Nome Completo"
            name="nomeAutor"
            value={dadosAutor.nomeAutor}
            onChange={handleChangeDadosAutor}
            fullWidth
            required
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="BI/Documento de Identidade"
            name="biAutor"
            value={dadosAutor.biAutor}
            onChange={handleChangeDadosAutor}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Telefone"
            name="telefoneAutor"
            value={dadosAutor.telefoneAutor}
            onChange={handleChangeDadosAutor}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="E-mail"
            name="emailAutor"
            value={dadosAutor.emailAutor}
            onChange={handleChangeDadosAutor}
            fullWidth
            type="email"
            required
          />
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 3 }} />
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>Informações Acadêmicas</Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Nome da Instituição"
            name="instituicao"
            value={dadosAcademicos.instituicao}
            onChange={handleChangeDadosAcademicos}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Supervisor"
            name="supervisor"
            value={dadosAcademicos.supervisor}
            onChange={handleChangeDadosAcademicos}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Co-Supervisor"
            name="coSupervisor"
            value={dadosAcademicos.coSupervisor}
            onChange={handleChangeDadosAcademicos}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Data de Defesa"
            name="dataDefesa"
            type="date"
            value={dadosAcademicos.dataDefesa}
            onChange={handleChangeDadosAcademicos}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Área do Conhecimento"
            name="areaConhecimento"
            value={dadosAcademicos.areaConhecimento}
            onChange={handleChangeDadosAcademicos}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Programa/Curso"
            name="programa"
            value={dadosAcademicos.programa}
            onChange={handleChangeDadosAcademicos}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <FormLabel>Titulação</FormLabel>
            <RadioGroup
              row
              name="tipoTitulacao"
              value={dadosAcademicos.tipoTitulacao}
              onChange={handleChangeDadosAcademicos}
            >
                <FormControlLabel value="Licenciatura" control={<Radio />} label="Licenciatura" />
              <FormControlLabel value="Mestrado" control={<Radio />} label="Mestrado" />
              <FormControlLabel value="Doutoramento" control={<Radio />} label="Doutoramento" />
            </RadioGroup>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <FormLabel>Tipo de Material</FormLabel>
            <RadioGroup
              row
              name="tipoMaterial"
              value={dadosAcademicos.tipoMaterial}
              onChange={handleChangeDadosAcademicos}
            >
               <FormControlLabel value="Monografia" control={<Radio />} label="Monografia" />
              <FormControlLabel value="Dissertação" control={<Radio />} label="Dissertação" />
              <FormControlLabel value="Tese" control={<Radio />} label="Tese" />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
  
  const renderEtapa4 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Direitos de Propriedade Intelectual</Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2">Importante</Typography>
        <Typography variant="body2">
          Antes de arquivar sua monografia no repositório digital, é necessário aceitar os termos abaixo.
        </Typography>
      </Alert>
      
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <FormControlLabel
          control={
            <Checkbox
              checked={aceitesLegais.aceiteDireitosAutorais}
              onChange={handleChangeAceitesLegais}
              name="aceiteDireitosAutorais"
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              <strong>Declaração de Direitos Autorais:</strong> Retenho todos os direitos de autor relativos à tese ou dissertação, e o direito de a usar em trabalhos futuros.
            </Typography>
          }
        />
      </Paper>
      
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <FormControlLabel
          control={
            <Checkbox
              checked={aceitesLegais.aceiteLicencaDistribuicao}
              onChange={handleChangeAceitesLegais}
              name="aceiteLicencaDistribuicao"
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              <strong>Licença de Permissão de Uso:</strong> Concedo à (NOME DA UNIVERSIDADE) permissão não-exclusiva para arquivar, manter e tornar acessível, na plataforma institucional, a minha tese ou dissertação, no todo ou em parte, em suporte digital. Autorizo a (NOME DA UNIVERSIDADE) a arquivar e, sem alterar o conteúdo, converter a tese ou dissertação entregue, para qualquer formato de ficheiro, meio ou suporte, nomeadamente através da sua digitalização, para efeitos de preservação e acesso.
            </Typography>
          }
        />
      </Paper>
      
      <Alert severity="warning">
        <Typography variant="body2">
          Ao clicar em &ldquo;Arquivar Documento&rdquo;, você confirma que leu e concorda com os termos acima e que as informações fornecidas são verdadeiras.
        </Typography>
      </Alert>
    </Box>
  );
  
  // Renderizar a etapa atual
  const renderEtapaAtual = () => {
    switch (etapaAtual) {
      case 0: return renderEtapa1();
      case 1: return renderEtapa2();
      case 2: return renderEtapa3();
      case 3: return renderEtapa4();
      default: return null;
    }
  };
  
  // Se ocorreu sucesso, mostrar mensagem
  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'success.light',
            mb: 3,
          }}
        >
          <CloudUpload sx={{ fontSize: 36, color: 'success.dark' }} />
        </Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Documento depositado com sucesso!
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
          O seu trabalho foi submetido ao repositório digital e já está disponível para consulta.
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ my: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Stepper activeStep={etapaAtual} sx={{ mb: 4 }}>
        <Step>
          <StepLabel>Upload</StepLabel>
        </Step>
        <Step>
          <StepLabel>Palavras-chave</StepLabel>
        </Step>
        <Step>
          <StepLabel>Dados do Autor</StepLabel>
        </Step>
        <Step>
          <StepLabel>Direitos e Permissões</StepLabel>
        </Step>
      </Stepper>
      
      {renderEtapaAtual()}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          onClick={voltarEtapa}
          disabled={etapaAtual === 0}
          startIcon={<BackIcon />}
        >
          Voltar
        </Button>
        
        {etapaAtual < totalEtapas - 1 ? (
          <Button 
            onClick={avancarEtapa}
            variant="contained" 
            color="primary"
            endIcon={<NextIcon />}
            disabled={!etapaValida()}
          >
            Próximo
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!etapaValida() || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Arquivar Documento'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

FormularioArquivamentoPublico.propTypes = {
  onSuccess: PropTypes.func,
  userAuthenticated: PropTypes.bool
};

export default FormularioArquivamentoPublico;